import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, Send, Component as ComponentIcon, MonitorPlay, Sparkles } from 'lucide-react';
import { api } from '../hooks/api.js';
import { useEditorStore, useEditorHistory } from '../stores/editorStore.js';
import { useAiComposer } from '../stores/aiComposer.js';
import { AiComposer } from '../components/ai/AiComposer.js';
import { EditorToolbar } from '../components/editor/EditorToolbar.js';
import { EditorCanvas } from '../components/editor/EditorCanvas.js';
import { InspectorPanel } from '../components/InspectorPanel.js';
import { PanelResizer } from '../components/PanelResizer.js';
import { SaveComponentModal } from '../components/SaveComponentModal.js';
import { PreviewModal } from '../components/PreviewModal.js';
import { PaneLoading } from '../components/PaneLoading.js';
import { useResizablePanel } from '../hooks/use-resizable-panel.js';
import { useEditorFonts } from '../hooks/useEditorFonts.js';
import { useEditorShortcuts } from '../hooks/useEditorShortcuts.js';
import { useEditorAutosave } from '../hooks/useEditorAutosave.js';
import { useDocumentDesign } from '../hooks/useDocumentDesign.js';
import type { Block, ComponentUsage } from '@blogforge/shared';
import { SERVER_URL } from '../lib/constants.js';

export function EditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const inspector = useResizablePanel();
  const qc = useQueryClient();

  // Keep the inspector overlay state in sync on small screens.
  useEffect(() => {
    document.body.classList.toggle('inspector-open', !inspector.collapsed);
    return () => document.body.classList.remove('inspector-open');
  }, [inspector.collapsed]);

  const { blocks, componentUsages, setBlocks, setComponentUsages, setPostId, markClean } =
    useEditorStore();
  const { undo, redo, canUndo, canRedo } = useEditorHistory();

  // Title lives in local state (committed to the server on blur).
  const [title, setTitle] = useState('');
  const [saveComponentOpen, setSaveComponentOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // ── Fetch post ────────────────────────────────────────────────
  const { data: post, isLoading } = useQuery({
    queryKey: ['post', id],
    queryFn: () => api.posts.get(id!),
    enabled: !!id,
  });

  // ── Shared editor plumbing (fonts, auto-save, shortcuts) ─────
  useEditorFonts();

  const { saveMutation, isDirty } = useEditorAutosave('post', id!);

  const aiComposer = useAiComposer();

  useEditorShortcuts({
    onSave: () => saveMutation.mutate(),
    undo,
    redo,
    onAi: () => aiComposer.openComposer(),
  });

  useEffect(() => {
    if (post && id) {
      setPostId(id);
      const typedPost = post as {
        blocks: string | Block[];
        componentUsages?: string | ComponentUsage[];
        title?: string;
      };
      const parsed: Block[] =
        typeof typedPost.blocks === 'string' ? JSON.parse(typedPost.blocks) : typedPost.blocks;
      const parsedUsages: ComponentUsage[] =
        typeof typedPost.componentUsages === 'string'
          ? JSON.parse(typedPost.componentUsages || '[]')
          : (typedPost.componentUsages ?? []);
      setBlocks(parsed);
      setComponentUsages(parsedUsages);
      setTitle(typedPost.title ?? '');
    }
  }, [post, id, setPostId, setBlocks, setComponentUsages]);

  // ── Publish ───────────────────────────────────────────────────
  const publishMutation = useMutation({
    mutationFn: async () => {
      // Save first, then publish
      await api.posts.update(id!, { blocks, componentUsages });
      return api.posts.publish(id!);
    },
    onSuccess: () => {
      markClean();
      void qc.invalidateQueries({ queryKey: ['post', id] });
      void qc.invalidateQueries({ queryKey: ['posts'] });
    },
  });

  // ── Preview ───────────────────────────────────────────────────
  // Persists the current editor state, then renders the post through the
  // real theme engine (drafts included) — no publish, no state change.
  const buildPreview = async (): Promise<string> => {
    await api.posts.update(id!, { blocks, componentUsages });
    const res = (await api.posts.preview(id!)) as { html?: string };
    if (!res.html) throw new Error('Preview returned no HTML');
    return res.html;
  };

  if (isLoading) {
    return <PaneLoading label="Loading post…" />;
  }

  const typedPost = post as Record<string, unknown> | undefined;
  // Preview the post with the design it will be built with (theme defaults ←
  // site tokens ← post overrides, plus the `maxWidth` column) — see
  // useDocumentDesign.
  const design = useDocumentDesign('post', id);

  return (
    <div
      className="editor-layout"
      style={{
        gridTemplateColumns: inspector.collapsed ? '1fr auto' : `1fr auto ${inspector.width}px`,
      }}
    >
      {/* ── Canvas side ─────────────────────────────────────── */}
      <div className="flex min-w-0 flex-col overflow-hidden">
        <EditorToolbar
          backTitle="posts"
          onBack={() => navigate('/posts')}
          onOpenInspector={() => inspector.setCollapsed(false)}
          title={{
            value: title,
            onChange: setTitle,
            onCommit: (value) => {
              void api.posts.update(id!, { title: value });
            },
            placeholder: 'Post title…',
          }}
          saveState={isDirty ? 'dirty' : saveMutation.isSuccess ? 'saved' : 'idle'}
          canUndo={canUndo}
          canRedo={canRedo}
          onUndo={undo}
          onRedo={redo}
          onSave={() => saveMutation.mutate()}
          canSave={isDirty}
          saving={saveMutation.isPending}
          actions={
            <>
              <button
                type="button"
                onClick={() => aiComposer.openComposer()}
                className="btn-ghost rounded-lg px-2.5 py-1.5 text-xs"
                title="Draft with AI (⌘J)"
              >
                <Sparkles size={13} className="text-accent" />
                Draft with AI
              </button>
              <button
                type="button"
                onClick={() => setPreviewOpen(true)}
                className="btn-ghost rounded-lg px-2.5 py-1.5 text-xs"
                title="Preview the rendered page (saves first)"
              >
                <MonitorPlay size={13} />
                Preview
              </button>
              <button
                type="button"
                onClick={() => setSaveComponentOpen(true)}
                disabled={blocks.length === 0}
                className="btn-ghost rounded-lg px-2.5 py-1.5 text-xs disabled:opacity-30"
                title="Save blocks as a reusable component"
              >
                <ComponentIcon size={13} />
                Save as component
              </button>
              {typedPost?.['status'] === 'published' && (
                <a
                  href={`${SERVER_URL}/blog/posts/${typedPost['slug'] as string}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost rounded-lg px-2.5 py-1.5 text-xs"
                >
                  <Eye size={13} />
                  View
                </a>
              )}
            </>
          }
          primaryAction={
            <button
              type="button"
              onClick={() => publishMutation.mutate()}
              disabled={publishMutation.isPending}
              className="btn-primary rounded-lg px-3 py-1.5 text-xs"
            >
              <Send size={13} />
              {typedPost?.['status'] === 'published' ? 'Update' : 'Publish'}
            </button>
          }
        />

        {/* Canvas */}
        <EditorCanvas contentKind="post" rawVariableDefinitions={undefined} design={design} />
      </div>

      {/* ── Inspector panel ──────────────────────────────────── */}
      <PanelResizer
        collapsed={inspector.collapsed}
        onResizeStart={inspector.startResize}
        onExpand={() => inspector.setCollapsed(false)}
      />
      {!inspector.collapsed && (
        <>
          {/* Tap-out scrim — only when the inspector overlays the canvas (small screens) */}
          <div
            className="bg-ink/30 fixed inset-0 z-[75] backdrop-blur-[2px] lg:hidden"
            onClick={inspector.toggleCollapsed}
            aria-hidden="true"
          />
          <InspectorPanel
            post={typedPost}
            postId={id!}
            onToggleCollapse={inspector.toggleCollapsed}
          />
        </>
      )}

      {/* ── Save-as-component ────────────────────────────────── */}
      {saveComponentOpen && (
        <SaveComponentModal contentKind="post" onClose={() => setSaveComponentOpen(false)} />
      )}

      {/* ── Rendered preview ─────────────────────────────────── */}
      {previewOpen && (
        <PreviewModal
          kind="post"
          title={title}
          onBuild={buildPreview}
          onClose={() => setPreviewOpen(false)}
        />
      )}

      {/* ── Draft with AI composer ───────────────────────────── */}
      {aiComposer.open && (
        <AiComposer
          contentKind="post"
          meta={{ title }}
          onMetaCommit={(patch) => {
            if (patch.title && patch.title !== title) {
              setTitle(patch.title);
              void api.posts.update(id!, { title: patch.title });
            }
          }}
          onClose={() => aiComposer.closeComposer()}
        />
      )}
    </div>
  );
}
